import { ShieldCheck, Activity, Zap, Workflow, Building2, ArrowRight, Play } from "lucide-react";
import { PILLARS, IMAGES } from "@/lib/site";

const ICONS = { ShieldCheck, Activity, Zap, Workflow, Building2 };

/**
 * Background Intelligence Core — Option C
 * Crystal rises from the bottom of the card, partial opacity, integrated as a
 * background object (not a placed image). Subtle sapphire glow + network lines.
 */
function BackgroundCrystal() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {/* Network lines & nodes behind crystal */}
      <svg
        className="absolute inset-0 w-full h-full opacity-70"
        viewBox="0 0 400 400"
        preserveAspectRatio="xMidYMax slice"
      >
        <defs>
          <radialGradient id="cta-node-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#9BC7FF" stopOpacity="1" />
            <stop offset="55%" stopColor="#3B82F6" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </radialGradient>
        </defs>
        {(() => {
          const nodes = [
            [70, 360], [120, 330], [180, 350], [240, 325], [300, 345], [340, 320],
            [90, 300], [160, 285], [230, 295], [290, 280],
            [200, 250],
          ];
          return (
            <>
              {nodes.map((a, i) =>
                nodes.slice(i + 1).map((b, j) => {
                  const dx = a[0] - b[0];
                  const dy = a[1] - b[1];
                  const d = Math.sqrt(dx * dx + dy * dy);
                  if (d > 110) return null;
                  return (
                    <line
                      key={`L-${i}-${j}`}
                      x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                      stroke="#60A5FA"
                      strokeWidth="0.6"
                      opacity={(1 - d / 110) * 0.55}
                    />
                  );
                })
              )}
              {nodes.map((n, i) => (
                <g key={i}>
                  <circle cx={n[0]} cy={n[1]} r="6" fill="url(#cta-node-glow)" />
                  <circle
                    cx={n[0]} cy={n[1]} r="1.5"
                    fill="#E0EBFF"
                    className="pulse-glow"
                    style={{ animationDelay: `${(i % 5) * 0.4}s` }}
                  />
                </g>
              ))}
            </>
          );
        })()}
      </svg>

      {/* Crystal glow halo */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[520px] h-[520px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.32), transparent 70%)" }}
      />

      {/* Crystal image — partially transparent, rising from bottom, behind content */}
      <img
        src={IMAGES.ctaCrystal}
        alt=""
        className="absolute left-1/2 -translate-x-1/2 bottom-[-8%] w-[460px] lg:w-[540px] h-auto object-contain ambient-pulse"
        style={{
          mixBlendMode: "screen",
          opacity: 0.78,
          WebkitMaskImage:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 26%, rgba(0,0,0,1) 100%)",
          maskImage:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 26%, rgba(0,0,0,1) 100%)",
        }}
        draggable="false"
      />

      {/* Soft fade to card background at top, ensures legibility behind text */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,61,0.90) 0%, rgba(13,27,61,0.60) 30%, rgba(13,27,61,0.10) 65%, rgba(13,27,61,0.0) 100%)",
        }}
      />
    </div>
  );
}

export default function WhyAndCTA({ onBookDemo }) {
  return (
    <section id="why" className="relative pb-20 lg:pb-24" data-testid="why-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.75fr_1fr] gap-6 lg:gap-7 items-stretch">
          {/* LEFT — Why Esthera pillars */}
          <div
            className="relative rounded-2xl overflow-hidden border border-white/[0.08] flex flex-col"
            style={{
              background:
                "linear-gradient(180deg, rgba(20,28,48,0.72) 0%, rgba(13,18,32,0.85) 100%)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
            <div className="p-8 lg:p-10">
              <div className="text-center">
                <div className="eyebrow" data-testid="why-eyebrow">
                  <span className="inline-block w-6 h-px bg-sapphire-glow/60 mr-3 align-middle" />
                  WHY REAL ESTATE LEADERS CHOOSE ESTHERA
                  <span className="inline-block w-6 h-px bg-sapphire-glow/60 ml-3 align-middle" />
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-7 gap-x-5">
                {PILLARS.map((p, i) => {
                  const Icon = ICONS[p.icon];
                  return (
                    <div
                      key={p.title}
                      className="group flex flex-col"
                      data-testid={`pillar-${i}`}
                    >
                      <div className="w-10 h-10 rounded-lg border border-sapphire-glow/30 bg-sapphire/[0.10] inline-flex items-center justify-center group-hover:border-sapphire-glow/60 transition-colors">
                        <Icon className="w-5 h-5 text-sapphire-glow" />
                      </div>
                      <div className="font-display mt-4 text-platinum text-[17px] leading-tight">
                        {p.title}
                      </div>
                      <div className="mt-2 text-[12px] text-platinum/55 leading-[1.55]">
                        {p.desc}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT — Ready to Transform — Option C crystal integration */}
          <div
            className="relative rounded-2xl overflow-hidden border border-white/[0.08] min-h-[420px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(13,27,61,0.92) 0%, rgba(10,15,26,0.96) 100%)",
            }}
            data-testid="final-cta-section"
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/60 to-transparent" />
            <BackgroundCrystal />

            <div className="relative p-8 lg:p-9">
              <div className="eyebrow">
                READY TO TRANSFORM YOUR<br />REAL ESTATE OPERATIONS?
              </div>
              <h3
                className="font-display mt-4 text-platinum text-[26px] lg:text-[30px] leading-[1.1] tracking-[-0.015em]"
                data-testid="final-cta-title"
              >
                <span className="text-sapphire-glow">Unify</span> your data, teams, and operations.
              </h3>
              <p className="mt-3 text-[13.5px] text-platinum/70 leading-[1.6] max-w-[320px]">
                See how Esthera RealityOS can unify your data, teams, and
                operations to drive measurable results.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={onBookDemo}
                  data-testid="final-cta-book-demo"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="final-cta-watch"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Watch Overview
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
